/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import { ArrowBack, Delete, Edit, FileCopy, Save } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useAuth } from 'auth/Auth';
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import { usePermission } from 'auth/usePermission';
import CustomObjectFieldRenderer from 'components/CustomObjectFields/CustomObjectFieldRenderer';
import { formatCellValue, validateRecordValues } from 'components/SettingsComponents/customObjectsFieldTypes';
import { formatDate } from 'utils/DateTimeConversion';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { resolveImageFeaturePath } from 'utils/customObjectImagePrivacy';
import { linkedValue } from "components/v2/ContactValue";

const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function AuditFieldValue({ fieldName, record, onUserClick }) {
  if (fieldName === 'created_by') {
    const name = record?.created_by_name || (record?.created_by != null ? `User #${record.created_by}` : '—');
    const userId = record?.created_by;
    return (
      <MDTypography
        component="span"
        variant="button"
        color={userId ? 'info' : 'text'}
        fontWeight="medium"
        onClick={() => userId && onUserClick(userId, name)}
        sx={{ cursor: userId ? 'pointer' : 'default', ml: 0.5 }}
      >
        {capitalizeFirstLetter(name)}
      </MDTypography>
    );
  }

  if (fieldName === 'updated_by') {
    const name = record?.updated_by_name || (record?.updated_by != null ? `User #${record.updated_by}` : '—');
    const userId = record?.updated_by;
    return (
      <MDTypography
        component="span"
        variant="button"
        color={userId ? 'info' : 'text'}
        fontWeight="medium"
        onClick={() => userId && onUserClick(userId, name)}
        sx={{ cursor: userId ? 'pointer' : 'default', ml: 0.5 }}
      >
        {capitalizeFirstLetter(name)}
      </MDTypography>
    );
  }

  if (fieldName === 'created_at' || fieldName === 'updated_at') {
    const formatted = formatDate(record?.[fieldName], 'datetime');
    return (
      <MDTypography component="span" variant="button" fontWeight="medium" sx={{ ml: 0.5 }}>
        {formatted || '—'}
      </MDTypography>
    );
  }

  return (
    <MDTypography component="span" variant="button" fontWeight="medium" sx={{ ml: 0.5 }}>
      {formatCellValue({ type: 'text', name: fieldName }, record?.[fieldName])}
    </MDTypography>
  );
}

export default function CustomObjectDetail() {
  const { apiName, recordId } = useParams();
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  // Previously unguarded: support users could edit and delete custom object
  // records even though the same actions are blocked on every standard object.
  const { canEdit, canDelete } = usePermission(`co_${apiName}`);
  const [definition, setDefinition] = useState(null);
  const [record, setRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // UI v2 record layout: page header with the same actions, sections in one card.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const [saving, setSaving] = useState(false);
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const sections = useMemo(() => definition?.sections || [], [definition]);
  const editableFields = useMemo(
    () => (sections || []).flatMap((s) => (s.fields || []).filter((f) => !f.readOnly && !['created_by', 'created_at', 'updated_by', 'updated_at'].includes(f.name))),
    [sections]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [defRes, recRes] = await Promise.all([
          axiosInstance.get(`/custom-objects/api/${apiName}`),
          axiosInstance.get(`/custom-objects/${apiName}/data/${recordId}`),
        ]);
        setDefinition(defRes.data);
        setRecord(recRes.data);
        setFormData(recRes.data);
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load record', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [axiosInstance, apiName, recordId]);

  const handleUserClick = (userId, userName) => {
    if (userId) {
      navigate(`/users/${userId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: `Unable to find user information for ${userName || 'this user'}.`,
        severity: 'info',
      });
    }
  };

  const save = async () => {
    const { isValid, fieldErrors: nextErrors, messages } = validateRecordValues(editableFields, formData);
    if (!isValid) {
      setFieldErrors(nextErrors);
      setSnackbar({ open: true, message: messages.join(' · '), severity: 'error' });
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/custom-objects/${apiName}/data/${recordId}`, formData);
      setRecord(res.data);
      setFormData(res.data);
      setEditing(false);
      setSnackbar({ open: true, message: 'Record updated', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update record',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axiosInstance.delete(`/custom-objects/${apiName}/data/${recordId}`);
      navigate(`/custom-objects/${apiName}`);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete record', severity: 'error' });
    }
  };

  const handleClone = async () => {
    setIsCloneLoading(true);
    try {
      const payload = {};
      editableFields.forEach((field) => {
        if (record?.[field.name] !== undefined && record?.[field.name] !== null) {
          payload[field.name] = record[field.name];
        }
      });
      const res = await axiosInstance.post(`/custom-objects/${apiName}/data`, payload);
      setSnackbar({
        open: true,
        message: 'Record cloned successfully',
        severity: 'success',
      });
      setTimeout(() => {
        navigate(`/custom-objects/${apiName}/${res.data.id}`);
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to clone record',
        severity: 'error',
      });
    } finally {
      setIsCloneLoading(false);
    }
  };

  // v2: URL and Email fields, and text fields labelled as a phone number,
  // read as links (components/v2/contactLinks.js).
  const linkedFieldValue = (field, value) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    const label = field.type === 'url' ? 'Website' : field.type === 'email' ? 'Email' : field.label;
    return linkedValue(label, value, null);
  };

  const renderFieldValue = (field) => {
    const isAuditUser = field.name === 'created_by' || field.name === 'updated_by';
    const isAuditDate = field.name === 'created_at' || field.name === 'updated_at';
    const isAudit = isAuditUser || isAuditDate || field.readOnly;

    if (editing && !field.readOnly && !isAuditUser && !isAuditDate) {
      return (
        <CustomObjectFieldRenderer
          field={field}
          value={formData[field.name]}
          error={fieldErrors[field.name]}
          onChange={(val) => {
            setFormData((prev) => ({ ...prev, [field.name]: val }));
            if (fieldErrors[field.name]) {
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field.name];
                return next;
              });
            }
          }}
          axiosInstance={axiosInstance}
          featurePath={resolveImageFeaturePath(apiName)}
        />
      );
    }

    if (isAuditUser || isAuditDate) {
      return (
        <MDBox display="flex" alignItems="center" flexWrap="wrap">
          <MDTypography variant="button" fontWeight="regular" color="text">
            <strong>{field.label}:</strong>
          </MDTypography>
          <AuditFieldValue fieldName={field.name} record={record} onUserClick={handleUserClick} />
        </MDBox>
      );
    }

    return (
      <MDBox>
        <MDTypography variant="caption" color="text" fontWeight="regular" display="block">
          {field.label}
        </MDTypography>
        {field.type === 'long_text' ? (
          <MDBox sx={{ mt: 0.5 }} dangerouslySetInnerHTML={{ __html: record?.[field.name] || '—' }} />
        ) : field.type === 'image' && (typeof record?.[field.name] === 'string' ? record[field.name] : record?.[field.name]?.url) ? (
          <MDBox sx={{ mt: 0.5 }}>
            <Box
              component="img"
              src={typeof record[field.name] === 'string' ? record[field.name] : record[field.name].url}
              alt={(typeof record[field.name] === 'object' && record[field.name].file_name) || field.label}
              sx={{
                maxWidth: 240,
                maxHeight: 180,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
          </MDBox>
        ) : (
          <MDTypography variant="body2" sx={{ display: 'block', mt: 0.5 }}>
            {(isV2 && linkedFieldValue(field, record?.[field.name])) || formatCellValue(field, record?.[field.name])}
          </MDTypography>
        )}
      </MDBox>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="default"
          title="Loading Custom Object"
          message="Please wait while we fetch the custom object details..."
        />
      </DashboardLayout>
    );
  }

  // Shared by the classic and v2 headers.
  const recordActions = (
    <>
            {editing ? (
              <>
                <MDButton color="secondary" onClick={() => { setEditing(false); setFormData(record); }}>Cancel</MDButton>
                <MDButton color="info" onClick={save} disabled={saving}><Save sx={{ mr: 0.5 }} /> {saving ? 'Saving...' : 'Save'}</MDButton>
              </>
            ) : (
              <>
                {canEdit && (
                  <MDButton color="info" onClick={() => setEditing(true)}><Edit sx={{ mr: 0.5 }} /> Edit</MDButton>
                )}
                <Tooltip title="Clone Record" placement="top">
                  <span>
                    <IconButton
                      onClick={handleClone}
                      disabled={isCloneLoading}
                      sx={{
                        ...commonIconButtonStyles,
                        position: 'relative',
                      }}
                    >
                      {isCloneLoading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            color: '#ffffff',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                          }}
                        />
                      ) : (
                        <FileCopy sx={commonIconStyles} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {canDelete && (
                  <MDButton color="error" onClick={remove}><Delete sx={{ mr: 0.5 }} /> Delete</MDButton>
                )}
              </>
            )}
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 0 : 6} pb={3}>
        {isV2 ? (
          <PageHeader title={`${definition?.name || ''} #${recordId}`} actions={recordActions} />
        ) : (
        <Card sx={{ p: 3, mb: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox display="flex" alignItems="center" gap={1}>
              <IconButton onClick={() => navigate(`/custom-objects/${apiName}`)}><ArrowBack /></IconButton>
              <MDTypography variant="h4">{definition?.name} #{recordId}</MDTypography>
            </MDBox>
            <MDBox display="flex" gap={1}>
              {recordActions}
            </MDBox>
          </MDBox>
        </Card>
        )}

        {isV2 ? (
          // v2: the sections sit flat in one card, as on the other records.
          <Card sx={{ p: 3 }}>
            {sections.map((section, i) => (
              <MDBox key={section.section_id || section.id} mb={i === sections.length - 1 ? 0 : 4}>
                <MDTypography variant="h6" mb={2}>{section.name}</MDTypography>
                <Grid container spacing={2.5}>
                  {(section.fields || []).map((field) => (
                    <Grid item xs={12} md={field.type === 'long_text' ? 12 : 6} key={field.name}>
                      <MDBox sx={{ pb: 1.25, borderBottom: '1px solid #E2E7EF', minWidth: 0 }}>{renderFieldValue(field)}</MDBox>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            ))}
          </Card>
        ) : sections.map((section) => (
          <Card key={section.section_id || section.id} sx={{ p: 3, mb: 2 }}>
            <MDTypography variant="h6" mb={2}>{section.name}</MDTypography>
            <Grid container spacing={2}>
              {(section.fields || []).map((field) => (
                <Grid item xs={12} md={field.type === 'long_text' ? 12 : 6} key={field.name}>
                  {renderFieldValue(field)}
                </Grid>
              ))}
            </Grid>
          </Card>
        ))}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
